<template>
    <breeze-authenticated-layout>
        <div class="py-2">
            <div class="mx-auto sm:px-6 lg:px-8">
                <div class="bg-white overflow-hidden shadow-sm sm:rounded-lg">
                    <div class="p-6 bg-white border-b border-gray-200">
                        <div class="mb-3 filters" v-if="godmode">
                            <inertia-link v-for="salesperson in salespeople"
                                          :href="`/offers?author=${salesperson.id}`"
                                          :class="{'bg-green-300 hover:bg-green-300' : selectedAuthor === salesperson.id}"
                                          class="rounded border py-1 px-2 text-sm mr-2 hover:bg-gray-100 cursor-pointer">
                                {{`${salesperson.name} (${salesperson.offers_count})`}}
                            </inertia-link>
                            <inertia-link v-if="selectedAuthor"
                                          href="/offers"
                                          class="rounded border py-1 px-2 text-sm mr-2 hover:bg-gray-100 bg-gray-100 cursor-pointer">
                                {{ $t('offersIndex.Reset') }}
                            </inertia-link>
                        </div>
                        <div class="mb-4">
                            <button class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded" @click="newOfferPanelShown = !newOfferPanelShown">{{ $t('offersIndex.New offer') }}</button>
                            <a :href="comparisonLink" target="_blank" v-show="compareOffers.length >1" class="bg-green-300 hover:bg-green-500 py-1 px-3 rounded float-right">{{ $t('offersIndex.Compare the offers') }}</a>
                        </div>
                        <div class="grid grid-cols-2 mb-5 border p-5" v-show="newOfferPanelShown">
                            <div>
                                <p>{{ $t('offersIndex.Choose from PipeDrive') }}</p>
                                <select name="newOffer" id="" v-model.number="newOfferLinkId">
                                    <option v-for="lead in leads" :value="lead.id">{{lead.id}}: {{lead.name}}</option>
                                </select>
                                <inertia-link :href="`/offers/create/${newOfferLinkId}`">
                                    <button class="ml-3 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-2 rounded" :disabled="!newOfferLinkId" :class="{'disabled:opacity-50':!newOfferLinkId}">{{ $t('offersIndex.Create') }}</button>
                                </inertia-link>
                            </div>
                            <div>
                                <p>{{ $t('offersIndex.Or create an offer from scratch') }}</p>
                                <inertia-link href="/offers/create">
                                    <button class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-2 rounded">{{ $t('offersIndex.Create') }}</button>
                                </inertia-link>
                            </div>
                        </div>
                        <table class="homeTable">
                            <thead>
                            <tr>
                                <th>ID</th>
                                <th>{{ $t('offersIndex.First name and last name') }}</th>
                                <th>{{ $t('offersIndex.Source') }}</th>
                                <th>{{ $t('offersIndex.Equipment') }}</th>
                                <th>{{ $t('offersIndex.Address') }}</th>
                                <th>{{ $t('offersIndex.Net') }}</th>
                                <th>{{ $t('offersIndex.Gross') }}</th>
                                <th>{{ $t('offersIndex.Date') }}</th>
                                <th v-if="godmode">{{ $t('offersIndex.Author') }}</th>
                                <th>{{ $t('offersIndex.Actions') }}</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="offer in offers.data" class="text-sm">
                                <td>{{offer.id}}</td>
                                <td>{{offer.clientName}}</td>
                                <td>
                                    <a v-if="offer.pdId" target="_blank" :href="`https://novaenergy.pipedrive.com/deal/${offer.pdId}`">Pipedrive</a>
                                    <span v-else>{{ $t('offersIndex.Apart from') }} PD</span>
                                </td>
                                <td class="text-xs">{{`${offer.panelsNumber} x ${offer.solar_panel.model} + ${offer.inverter.model}`}}</td>
                                <td>{{offer.clientAddress}}</td>
                                <td>{{$numberHelper.formatMoney(offer.totalPriceWithMarginNet)}}</td>
                                <td>{{$numberHelper.formatMoney(offer.totalPriceWithMarginWithVAT)}}</td>
                                <td> {{ moment(offer.created_at).format("DD/MM/YY h:mm") }}</td>
                                <td v-if="godmode">{{offer.author.name}}</td>
                                <td>
                                    <a target="_blank" :href="`/offers/pdf/${offer.id}`">
                                        <button class="bg-blue-500 hover:bg-blue-700 text-white font-bold text-sm px-3 rounded">PDF</button>
                                    </a>
                                    <inertia-link :href="`/offers/${offer.id}/edit`">
                                        <button class="bg-purple-600 font-bold hover:bg-purple-900 ml-2 px-3 rounded text-sm text-white">{{ $t('offersIndex.Edit') }}</button>
                                    </inertia-link>
                                    <button class="ml-3"
                                            :class="{'text-green-500': compareOffers.includes(offer.id), 'disabled:opacity-30':!newOfferLinkId}"
                                            @click="toggleOfferToCompare(offer.id)"
                                            :disabled="!offersAvailableToCompare.includes(offer.id)"
                                    >
                                        <svg class="w-6 h-6 inline-block text-sm" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                                        </svg>
                                        <span class="inline-block ml-1 text-sm">{{ $t('offersIndex.Compare') }}</span>
                                    </button>
                                </td>
                            </tr>
                            </tbody>
                        </table>

                        <div v-if="offers.links.length > 3">
                            <div class="flex flex-wrap my-5">
                                <template v-for="(link, k) in offers.links" :key="k">
                                    <div v-if="link.url === null"  class="mr-1 mb-1 px-4 py-3 text-sm leading-4 text-gray-400 border rounded" v-html="link.label" />
                                    <inertia-link v-else class="mr-1 mb-1 px-4 py-3 text-sm leading-4 border rounded hover:bg-white focus:border-indigo-500 focus:text-indigo-500" :class="{ 'bg-blue-700 text-white': link.active }" :href="link.url" v-html="link.label" />
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </breeze-authenticated-layout>
</template>

<script>

    import BreezeAuthenticatedLayout from '@/Layouts/Authenticated'
    import moment from 'moment';

    export default {
        props: {
            offers: {
                type: Object,
                required: true
            },
            leads: {
                type: Object,
                required: true
            },
            salespeople: {
                type: Array,
                required: true
            },
            selectedAuthor: {
                type: Number,
                required: false
            },
        },
        components: {
            BreezeAuthenticatedLayout,
            moment,
        },
        data() {
            return {
                godmode: this.$page.props.auth.user.role !== 'sales',
                moment: moment,
                newOfferLinkId: null,
                newOfferPanelShown: false,
                compareOffers: [],
            }
        },
        computed: {
            offersAvailableToCompare() {
                let offers = this.offers.data;
                if (this.compareOffers.length === 0) {
                    return offers.map(o => o['id']);
                } else {
                    let firstOffer = offers.find((o) => {
                            return o.id === this.compareOffers[0]
                        }),
                        offersOnSameAddress = offers.filter((o) => {
                            return o.clientAddress === firstOffer.clientAddress
                        });
                    return offersOnSameAddress.map(o => o['id']);
                }
            },
            comparisonLink() {
                return '/offers/compare/' + this.compareOffers.join(',')
            }
        },
        methods: {
            toggleOfferToCompare(id) {
                if (this.compareOffers.includes(id)) {
                    var index = this.compareOffers.indexOf(id);
                    if (index !== -1) {
                        this.compareOffers.splice(index, 1);
                    }
                } else {
                    if (this.compareOffers.length <= 3) {
                        this.compareOffers.push(id);
                    } else {
                        alert(this.$t('offersIndex.select up to 4 offers'))
                    }
                }
            }
        }
    }
</script>

<style lang="scss">
    .homeTable {
        width: 100%;

        th {
            text-align: left;
        }

        tr:nth-child(even) {
            background-color: #f3f4f6;
        }

        td {
            padding: 1px 3px;
        }
    }
</style>
